import {isFunction, isNullish} from './Utils';

/**
 * @returns {value is number} `true` if the given value is {@link isNotNullish} and is type of `number`.
 */
export const isNumber = (value: unknown): value is number => !isNullish(value) && typeof value === 'number';

/**
 * Checks if a value is a finite number (not NaN, not Infinity, and not -Infinity).
 *
 * This function should be used instead of directly calling `Number.isFinite` or the global `isFinite`
 * because it provides ES5 compatibility and proper type checking.
 *
 * **Why not use `Number.isFinite` directly?**
 * `Number.isFinite` is an ES6 method and does not exist in ES5 environments. Calling it directly
 * would throw a `TypeError` at runtime in older environments.
 *
 * **Key difference between `Number.isFinite` and global `isFinite`:**
 * - `Number.isFinite(value)` does NOT coerce the value — it returns `true` only if the value is
 *   already a number and is finite. For example, `Number.isFinite("123")` returns `false`.
 * - Global `isFinite(value)` DOES coerce the value to a number before checking. For example,
 *   `isFinite("123")` returns `true` because `"123"` is coerced to `123`.
 *
 * This function avoids the coercion risk by first checking the type with `isNumber`, making both
 * implementations behave consistently regardless of the environment.
 *
 * @param value - The value to check
 * @returns `true` if the value is a finite number, `false` otherwise (including NaN, Infinity, -Infinity, null, undefined, or non-numeric types)
 *
 * @example
 * isFiniteNumber(123) // true
 * isFiniteNumber(0) // true
 * isFiniteNumber(-456.78) // true
 * isFiniteNumber(NaN) // false
 * isFiniteNumber(Infinity) // false
 * isFiniteNumber(-Infinity) // false
 * isFiniteNumber("123") // false (string, not coerced)
 * isFiniteNumber(null) // false
 * isFiniteNumber(undefined) // false
 */
export const isFiniteNumber = (value: unknown): value is number => {
  if (!isNumber(value)) {
    return false;
  }

  // our target is ES5, so we can't use Number.isFinite without checking for it
  if (isFunction(Number.isFinite)) {
    return Number.isFinite(value);
  }

  // Fallback implementation for environments that do not support Number.isFinite
  return isFinite(value);
};
